import {useImgDescs, usePeople, useUpdateImgDesc} from "../hooks/hooks_index";
import {Loading, MediaImage, MediaImageLoading, MediaIconLoading} from "../components/components_index";
import { useState, useRef, useEffect } from "react";
import {Link, useParams} from "react-router-dom";
import '../styles/ImgZoom.css';

export default function ImgZoom ({src, alt, className, img_array, bioPic=false, isEdit=false, editingImgs=false, editAll=false}) {
  // src: image url - eg. "S_logo_PNG.png"
  // alt: subtext
  // className: CSS classname for individual image
  // img_array: other image urls in gallery array
  // isEdit: true if the image desc can be edited by the user
  // editingImgs: disabled zooming if the gallery is in edit mode
  // editAll: true if admin is in the EditAll images page, allows admin to set scrollbox imgs 
  
  const {data: image_infos, isLoading: imgLoading, error: imgError} = useImgDescs();
  const {data: people, isLoading: peopleLoading, error: peopleError} = usePeople();

  const [imgSelected, setImgSelected] = useState(null);

  if (peopleLoading || imgLoading) {
    return (
      <div className="loading-overlay">
        <div className="spinner" />
      </div>
    )}
  if (peopleError || imgError) return <div>Error</div>;

  if (!people || !image_infos) return <Loading />;
  
  return (
    <div className="image-zoom-container">
      {!bioPic &&
        <div className='image-zoom-button' onClick={() => !editingImgs?setImgSelected(true):undefined}>
          {/* <MediaImage mediaID={src} alt={alt} className={className} /> */}
          {img_array && <MediaIconLoading src={src} className={className} />}
          {!img_array && <MediaImageLoading src={src} className={className} />}
        </div>
      }
      {bioPic &&
        <div className='image-zoom-button' onClick={() => !editingImgs?setImgSelected(true):undefined}>
          <MediaImage mediaID={src} alt={alt} className={className} />
          {/* <MediaImageLoading src={src} className={className} /> */}
        </div>
      }
      {imgSelected &&
        <ImagePopup image={src} alt={alt} onClose={() => setImgSelected(false)} imgInfos={image_infos} peopleList={people} img_array={img_array} isEdit={isEdit} editAll={editAll}/>
      }
    </div>
    
  )
}

function ImagePopup ({image, alt, onClose, imgInfos, peopleList, img_array, isEdit, editAll}) { 
  const mult_imgs = Array.isArray(img_array);

  let startingIdx 
  if (mult_imgs) {
    startingIdx = img_array.findIndex (
      img => img.media_id === image
    )
  }

  const popupRef = useRef(null);
  const [currIdx, setCurrIdx] = useState(startingIdx);
  const [isEditing, setIsEditing] = useState(false);
  
  const nextImage = () => {
    setCurrIdx((currIdx + 1) % img_array.length);
  };

  const prevImage = () => {
    setCurrIdx((currIdx - 1 + img_array.length) % img_array.length);
  };

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [image]);

  if (!image) return null;

  return (
    <div
      className={'image-zoom-popup'}
      onClick={isEditing ? undefined : onClose}
      ref={popupRef}
    >
      <div className={`image-zoom-popup-box${isEditing?' editing-popup':''}`} onClick={e => e.stopPropagation()}>
        {!isEditing && <button className='close-zoom-popup' onClick={onClose}>{'\u00D7'}</button>}
        {mult_imgs &&
          <div className="mult-imgs"> 
            <div className="index-number">Gallery Photo # {currIdx + 1} / {img_array.length}</div> 
            <ImageContent image={img_array[currIdx].media_id} alt={alt} key={currIdx} imgInfos={imgInfos} peopleList={peopleList} isEdit={isEdit} isEditing={isEditing} setIsEditing={setIsEditing} editAll={editAll}/>
            {!isEditing &&
              <div>
                <button className='scroll-button-mult back-button-mult' onClick={prevImage} disabled={img_array.length <= 1}>
                  {'\u2039'}
                </button>
                <button className='scroll-button-mult next-button-mult' onClick={nextImage} disabled={img_array.length <= 1}>
                  {'\u203A'}
                </button>
              </div>
            }
            {isEdit && !isEditing &&
              <div className="edit-uploaded-buttons">
                <button 
                  className={`${isEditing?"editing-button":"edit-button"}`}
                  onClick={() => setIsEditing(!isEditing)}
                >
                  {`${isEditing?"Cancel":"Edit"}`}
                </button>
              </div>
            }
          </div>
        }
        {!mult_imgs &&
          <ImageContent image={image} alt={alt} imgInfos={imgInfos} peopleList={peopleList} editAll={editAll}/>
        }
      </div>
    </div>
  )
}

function ImageContent({image, alt, key, imgInfos, peopleList, isEdit, isEditing, setIsEditing, editAll}) {
  const {personID} = useParams();
  const subtext = imgInfos[image]?.subtext ?? imgInfos[image]?.img_name?.split('.')[0];
  const people = imgInfos[image]?.people ?? [""];
  const {month, day, year} = imgInfos[image]?.date ?? {};
  const reunion = imgInfos[image]?.reunion ?? null;
  const saved_date = imgInfos[image]?.saved_date ?? null;
  const uploaded_by = imgInfos[image]?.uploaded_by ?? null;
  const img_name = imgInfos[image]?.img_name ?? null;
  const scroll_box = imgInfos[image]?.scroll_box ?? null;
  const profile_pic = imgInfos[image]?.profilePic ?? null;
  const show_id = editAll ? (image ?? null) : null;
  const [isUploading, setIsUploading] = useState(false);

  const updateImgDesc = useUpdateImgDesc();

  const sortedPeopleOptions = Object.entries(peopleList).sort(
    ([, personA], [, personB]) => {
      const nameA = (
        personA.AKA || `${personA.f_name} ${personA.l_name}`
      ).toLowerCase();
      const nameB = (
        personB.AKA || `${personB.f_name} ${personB.l_name}`
      ).toLowerCase();

      return nameA.localeCompare(nameB);
    }
  );

  const [selectedDesc, setSelectedDesc] = useState({
    subtext: subtext,
    people: people,
    date: {
      month: Number(month),
      day: Number(day),
      year: Number(year)
    },
    reunion: reunion,
    saved_date: saved_date,
    uploaded_by: uploaded_by,
    img_name: img_name,
    scroll_box: scroll_box
  });
  const [isLeapYear, setIsLeapYear] = useState(false);
  const [dayArray, setDayArray] = useState(() => {
    if ([1,3,5,7,8,10,12].includes(Number(month))) {
      return([
        "", 
        ...Array.from({length: 31}, (_, i) => i+1),
      ]);
    } else if ([4,6,9,11].includes(Number(month))) {
      return([
        "", 
        ...Array.from({length: 30}, (_, i) => i+1),
      ]);
    } else if (Number(month) === 2 && isLeapYear) {
      return([
        "", 
        ...Array.from({length: 29}, (_, i) => i+1),
      ]);
    } else if (Number(month) === 2) {
      return([
        "", 
        ...Array.from({length: 28}, (_, i) => i+1),
      ]);
    } else {
      return([]);
    }
  });
  const inputRef = useRef(null);
  const [peopleNames, setPeopleNames] = useState([]);

  const MONTHS = [
    "", "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const YEARS = [
    "", 
    ...Array.from(
      { length: new Date().getFullYear() - 1900 + 1 },
      (_, i) => new Date().getFullYear() - i
    ),
  ];

  const keyVisuals = {
    "people": "People:",
    "date": {
      "month": "Month",
      "day": "Day",
      "year": "Year"
    },
    "reunion": "Was this at the Reunion",
    "subtext": "Image Description",
    "scroll_box": "On Home Scroll Box",
    "profilePic": "Profile Pic"
  }

  async function handleUploadAll() {
    setIsUploading(true);
    try {
      const cleanedDesc = cleanDescs(selectedDesc)

      await updateImgDesc.mutateAsync({
        id: image,
        data: {
          ...cleanedDesc
        }
      })

          // if (!validIDPhoto(index, "disabled") && cleanedDesc["profilePic"]) {
          //   const selectedID = cleanedDesc["people"][0]
          //   await updatePerson.mutateAsync({
          //     id:selectedID,
          //     data: {
          //       ...people[selectedID],
          //       profile_pic: response.media_id
          //     }
          //   });
          // }

      setIsEditing(false)
    } catch (err) {
      console.error("Upload failed", err)
    }
    setIsUploading(false);
  }

  function cleanDescs(data) {
    const cleaned = {};

    for (const [key, value] of Object.entries(data)) {
      // console.log(`${key}: ${value}`)
      if (value === "" || value === null || value === undefined || value === false) continue;
      if (Array.isArray(value)) {
        if (value.length === 0) continue;

        const cleanedArray = value
          .map(item => (typeof item === 'object' ? cleanDescs(item) : item))
          .filter(item =>
            typeof item === 'object'
              ? Object.keys(item).length > 0
              : item !== ""
          );
        if (cleanedArray.length === 0) continue;
        cleaned[key] = cleanedArray;
        continue
      }
      if (typeof value === "object") {
        // if (Object.values(value).includes(0)) continue;
        const cleanedObj = cleanDescs(value);
        if (Object.keys(cleanedObj).length === 0) continue;
        cleaned[key] = cleanedObj;
        continue;
      } 
      if (key === "reunion") {
        if (value) {
          cleaned[key] = `R${data["date"]["year"]}`;
        }
        continue;
      }
      if (key === "profilePic") {
        if (value) {
          cleaned[key] = `${data["people"][0]}_profile_pic`;
        }
        continue;
      }
      cleaned[key] = value;
    } 
    // console.log(cleaned);
    return cleaned;
  }




  function updateField(key, value) {
    setSelectedDesc(prev => ({
      ...prev,
      [key]: value
    }));
  }

  const getArray = (key) => {
    const arr = selectedDesc[key];
    return Array.isArray(arr) ? arr : [""];
  };

  const updateArrayItem = (key, index, value) => {
    const arr = getArray(key);
    const newArr = [...arr];
    newArr[index] = value;
    updateField(key, newArr);
  };

  const addArrayItem = (key) => {
    const arr = getArray(key);
    updateField(key, [...arr, ""]);
  };

  const removeArrayItem = (key, index) => {
    if (index > 0) {
      const arr = getArray(key);
      const newArr = arr.filter((_, i) => i !== index);
      updateField(key, newArr);
    }
  };

  const getObject = (key, subKeys) => {
    const obj = selectedDesc[key] || {};
    const normalized = {};
    subKeys.forEach(subKey => {
      normalized[subKey] = obj?.[subKey] ?? "";
    });
    return normalized;
  };

  const updateObjectItem = (key, subKey, value) => {
    setSelectedDesc(prev => {
      const obj = prev[key] || {};

      const newDesc = {
        ...obj,
        [subKey]: (key==="date" && value === 0)?null:value
      }

      if (subKey === "year") {
        if (value % 4 === 0) {
          setIsLeapYear(true);
        }
      }

      if (subKey === "month") {
        if ([1,3,5,7,8,10,12].includes(value)) {
          setDayArray([
            "", 
            ...Array.from({length: 31}, (_, i) => i+1),
          ]);
        } else if ([4,6,9,11].includes(value)) {
          setDayArray([
            "", 
            ...Array.from({length: 30}, (_, i) => i+1),
          ]);
        } else if (value === 2 && isLeapYear) {
          setDayArray([
            "", 
            ...Array.from({length: 29}, (_, i) => i+1),
          ]);
        } else if (value === 2) {
          setDayArray([
            "", 
            ...Array.from({length: 28}, (_, i) => i+1),
          ]);
        } else {
          setDayArray([]);
          updateObjectItem("date", "day", null)
        }
      }

      return {
        ...prev,
        [key]: newDesc
      };
    })
  };

  const validReunion = (type) => {
    const year = selectedDesc.date?.year || false;

    const isDisabled = !year;

    if (type === "disabled") {
      return isDisabled;
    } 
    if (!isDisabled) {
      return "";
    }
    if (isDisabled) {
      return "Input the year";
    }
  }

  const renderField = (key) => {
    if (key === "people") {
      const arr = getArray(key);
      return(
        // <div className="edit-input array-input">
        //   <label>{keyVisuals[key]}</label>
        //   {arr.map((val,i) => (
        //     <SearchBar
        //       peopleNames={peopleNames}
        //       inputRef={inputRef}
        //       arrKey={key} 
        //       i={i}
        //       updateArrayItem = {updateArrayItem}
        //       val={val}
        //     />
        //   ))}
        //   <div>
        //     <button
        //       type="button"
        //       className="sub-button add-array-item"
        //       onClick={() => addArrayItem(key)}
        //     >
        //       Another
        //     </button>
        //     <button
        //       type="button"
        //       className="sub-button remove-array-item"
        //       onClick={() => removeArrayItem(key, arr.length-1)}
        //     >
        //       Delete last
        //     </button>
        //   </div>
        // </div>
        <div className="filter-row edit-input array-input people-input">
          {/* <select 
            id="people-filter"
            value=""
            onChange={(e) => {
              updateArrayItem(imgIndex, key, arr.length, e.target.value)
              e.target.value = "";
            }}
          >
            <option value="">
              Select a person...
            </option>
            {sortedPeopleOptions
              .filter(([id]) => !arr.includes(id) && !id.startsWith("P0"))
              .map(([id, person]) => (
                <option key={id} value={id}>
                  {person.AKA || `${person.f_name} ${person.l_name}`}
                </option>
              ))
            }
          </select> */}
          <div className="people-row-1">
            <label htmlFor="people-filter" className="people-label">
              People:
            </label>
            <input
              id="people-filter"
              className="people-filter"
              list="people-options"
              placeholder="Select or type a name..."
              onChange={(e) => {
                const value = e.target.value;
                const person = sortedPeopleOptions.find(([id, person]) =>
                  (person.AKA || `${person.f_name} ${person.l_name}`) === value
                );

                if (person) {
                  updateArrayItem(key, arr.length, person[0]);
                  e.target.value = "";
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && e.target.value.trim()) {
                  const value = e.target.value.trim();
                  const person = sortedPeopleOptions.find(([id, person]) =>
                    (person.AKA || `${person.f_name} ${person.l_name}`) === value
                  );

                  if (!person) {
                    updateArrayItem(key, arr.length, value);
                    e.target.value = "";
                  }
                }
              }}
            />

            <datalist id="people-options">
              {sortedPeopleOptions
                .filter(([id]) => !arr.includes(id) && !id.startsWith("P0"))
                .map(([id, person]) => (
                  <option 
                    key={id}
                    value={person.AKA || `${person.f_name} ${person.l_name}`}
                  />
                ))}
            </datalist>
          </div>

          {arr.length > 0 && (
            <div className="selected-people">
              {arr.map((id,i) => {
                const person = peopleList[id];
                const name = person ? person.AKA || `${person.f_name} ${person.l_name}` : id;
                if (!name) return null;
                return (
                  <div key={i} className="selected-person">
                    <button 
                      className="selected-person-button" 
                      onClick={() => removeArrayItem(key, i)}
                    >
                      {name}
                    </button>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      );
    } else if (key === "date") {
      let subKeys = [];
      let keyName = "";
      if (key === "date") {
        subKeys = ["month", "day", "year"];
        keyName = "Date Taken";
      }
      const obj = getObject(key, subKeys);
      return(
        <div className={`edit-input object-input date-input`} key={key}>
          <label>{keyName}:</label>
          {subKeys.map((subKey) => (
            <div className="edit-input object-element" key={subKey}>
              <div className="sub-label">{keyVisuals[key][subKey]}</div>
              {subKey === "month" && 
                <select
                  className={`sub-input date-subinput ${subKey}-box`}
                  value={obj[subKey]}
                  onChange={(e) => {updateObjectItem(key, subKey, e.target.value ===""?null:Number(e.target.value))}}
                >
                  {MONTHS.map((month, index) => (
                    <option key={month} value={index}>
                      {month}
                    </option>
                  ))}
                </select>   
              }
              {subKey === "day" && 
                <select
                  className={`sub-input date-subinput ${subKey}-box ${dayArray.length ? "" : "day-disabled"}`}
                  value={obj[subKey]}
                  disabled={dayArray.length === 0}
                  onChange={(e) => {updateObjectItem(key, subKey, e.target.value ===""?null:Number(e.target.value))}}
                >
                  {dayArray.map(day => (
                    <option key={day} value={day}>
                      {day}
                    </option>
                  ))}
                </select>   
              } 
              {subKey === "year" && 
                <select
                  className={`sub-input date-subinput ${subKey}-box`}
                  value={obj[subKey]}
                  onChange={(e) => {updateObjectItem(key, subKey, e.target.value ===""?null:Number(e.target.value))}}
                >
                  {YEARS.map(year => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>   
              } 
            </div>
          ))}    
        </div>
      )
    } else if (key === "reunion") {
      // return(
      //   <div className="single-input">
      //     <label className={`single-label`}>{keyVisuals[key]}:</label>
      //     <input
      //       type="text"
      //       value={selectedDesc[key] ?? ""}
      //       onChange={(e) => updateField(key, e.target.value)}
      //       autoComplete='batman'
      //       className={`${key}-box`}
      //     />
      //   </div>
      // )
      return(
        <div className="profile-pic" title={validReunion("hover")}>
          <label className={`single-label${validReunion("disabled") ? "-disabled" : ""}`}>{keyVisuals[key]}?</label>
          <input
            type='checkbox'
            className='pic-check'
            checked={selectedDesc[key] ?? false}
            onChange={(e) => updateField(key, e.target.checked)}
            disabled={validReunion("disabled")}
          />
        </div>
      )
    } else if (key === "subtext") {
      return(
        <div className="subtext-input">
          <label className={`single-label`}>{keyVisuals[key]}:</label>
          <textarea
            value={selectedDesc[key] ?? ""}
            rows={1}
            onInput={(e) => {
              e.target.style.height = "auto";
              e.target.style.height = e.target.scrollHeight + "px";
            }}
            onChange={(e) => updateField(key, e.target.value)}
            autoComplete='batman'
            className={`${key}-box`}
            placeholder="What's in this photo"
          />
        </div>
      )
    } else if (key === "scroll_box") {
      return(
        <div className="profile-pic">
          <label className={`single-label`}>{keyVisuals[key]}?</label>
          <input
            type='checkbox'
            className='pic-check'
            checked={selectedDesc[key] ?? false}
            onChange={(e) => updateField(key, e.target.checked)}
          />
        </div>
      )
    } else if (key === "profilePic") {
      return(
        <div className="profile-pic">
          <label className={`single-label`}>{keyVisuals[key]}?</label>
          <input
            type='checkbox'
            className='pic-check'
            checked={selectedDesc[key] ?? false}
            onChange={(e) => updateField(key, e.target.checked)}
            // disabled={validIDPhoto(imgIndex, "disabled")}
          />
        </div>
      )
    }
  }

  useEffect(() => {
    if (!peopleList) return;

    setPeopleNames(
      Object.entries(peopleList).map(([id, person]) => ({
        id,
        name: person.AKA || `${person.f_name} ${person.l_name}`,
        profile_pic: person.profile_pic
      }))
    );
  }, [peopleList]);

  // function ImgLoading({src, alt, className}) {
  //   const [loading, setLoading] = useState(true);
  //   return (
  //     <>
  //       {loading && (
  //         <Loading /> 
  //       )}
  //       <img
  //         src={src}
  //         alt={alt}
  //         className={className}
  //         onLoad={() => setLoading(false)}
  //       />
  //     </>
  //   )
  // }

  return (     
    <div className="image-content" ref={inputRef} key={key}>
      <MediaImageLoading className={`popup-img${isEditing ? '-editing' : ''}`} src={image} containerName={"popup-img"}/>
      {/* <MediaImage className={`popup-img${isEditing ? '-editing' : ''}`} mediaID={image}/> */}
      <div className='popup-content'>
        {!isEditing &&
          <div>
            {subtext &&
              <div className="popup-title-word popup-subtext-word popup-row">{subtext}</div>
            }
            {people.length !== 0 && people[0] !== "" &&
              <div className="popup-list popup-row">
                <span className="popup-title-word">People:  </span> 
                {people.map((person, idx) => {
                  const label =
                    peopleList[person]?.AKA?.split(" ")[0] ||
                    peopleList[person]?.f_name ||
                    person;

                  const isLink = peopleList[person] && person !== personID;

                  return (
                    <span key={person}>
                      {isLink ? (
                        <Link to={`/people/${person}`}>
                          {label}
                        </Link>
                      ) : (
                        <span>{label}</span>
                      )}

                      {idx < people.length - 1 && ", "}
                    </span>
                  );
                })}
              </div>
            }
            {reunion &&
              <div className="popup-title-word popup-row">{year} Reunion</div>
            }
            {year &&
              <div className="popup-row">
                <span className="popup-title-word">Date:  </span>
                <span>{month ? `${MONTHS[Number(month)]} ` : ''}{day ? `${Number(day)}, ` : ''}{year ?? ''}</span>
              </div>
            }
            {saved_date &&
              <div className="popup-row">
                <span className="popup-title-word">Uploaded On:  </span>
                <span>{`${MONTHS[Number(saved_date.split("-")[1])]} `}{`${Number(saved_date.split("-")[2].split("T")[0])}, `}{saved_date.split("-")[0]}</span>
              </div>
            }
            {uploaded_by &&
              <div className="popup-row">
                <span className="popup-title-word">Uploaded By:  </span>
                <span>{peopleList[uploaded_by]?.AKA || `${peopleList[uploaded_by]?.f_name} ${peopleList[uploaded_by]?.l_name}` || uploaded_by}</span>
                {/* <span>{` on ${monthNames[saved_date.split("-")[1]-1]} `}{`${Number(saved_date.split("-")[2])}, `}{saved_date.split("-")[0]}</span> */}
              </div>
            }
            {scroll_box &&
              <div className="popup-title-word popup-row">In the Home Page Scroll Box</div>
            }
            {show_id &&
              <div className="popup-title-word popup-row id-row">ID: {show_id}</div>
            }
            {show_id && profile_pic &&
              <div className="popup-title-word popup-row profile-pic-row">{profile_pic}</div>
            }
          </div>
        } 
        {isEditing &&
          <div className="updating-uploaded">
            <div className="update-field-group">
              {renderField("people", inputRef)}
              {renderField("date")}
              {renderField("reunion")}
              {renderField("subtext")}
              {editAll ? renderField("scroll_box") : undefined}
              {editAll ? renderField("profilePic") : undefined}
            </div>
            <div className="edit-uploaded-buttons">
              <button 
                className={`${isEditing?"editing-button":"edit-button"}`}
                onClick={() => setIsEditing(!isEditing)}
              >
                {`${isEditing?"Cancel":"Edit"}`}
              </button>
              {isEditing &&
                <button className="input-button upload" onClick={handleUploadAll}>Save</button>
              }
            </div>
          </div>
        }
        {isUploading &&
          <Loading word="Saving" />
        }
      </div>
    </div>
  )
}

function SearchBar ({peopleNames, inputRef, arrKey, i, updateArrayItem, val}) {
  const val_match = peopleNames.find(p => p.id === val);
  const [query, setQuery] = useState(val_match ? val_match.name : val?? "");
  const [suggestions, setSuggestions] = useState([]);
  const wrapperRef = useRef(null); 
  const popupRef = useRef(null);
  
  useEffect(() => {
    const inputBox = inputRef.current;
    if (!inputBox) return;

    function handleClick(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setSuggestions([]);
      }
    }

    document.addEventListener("mousedown", handleClick);

    return () => {
      document.removeEventListener("mousedown", handleClick);
    };
  }, []);


  useEffect(() => {
    const popupEl = popupRef.current;
    if (!popupEl) return;

    function lockScroll(e) {
      const { scrollTop, scrollHeight, clientHeight } = popupEl;
      const atTop = scrollTop === 0;
      const atBottom = scrollTop + clientHeight >= scrollHeight;

      const scrollingUp = e.deltaY < 0;
      const scrollingDown = e.deltaY > 0;
 
      if ((scrollingUp && atTop) || (scrollingDown && atBottom)) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      e.stopPropagation();
    }

    popupEl.addEventListener("wheel", lockScroll, { passive: false });

    return () => {
      popupEl.removeEventListener("wheel", lockScroll);
    };
  }, [suggestions]);
  
  useEffect(() => {
    const match = peopleNames.find(p => p.id === val);
    setQuery(match?.name ?? val ?? "");
  }, [val, peopleNames]);

  function handleChange(e) {
    const value = e.target.value;
    setQuery(value);

    updateArrayItem(arrKey, i, value);

    if (!value.trim()) {
      setSuggestions([]);
      return;
    }

    setSuggestions(
      peopleNames.filter(p =>
        p.name.toLowerCase().includes(value.toLowerCase())
      )
    );
  }

  function handleSuggestionClick(person) {
    setQuery(person.name);
    setSuggestions([]);

    updateArrayItem(arrKey, i, person.id)
  }

  useEffect(() => {
    if (!peopleNames) return;

    const match = peopleNames.find(
      p => p.name.toLowerCase() === query.toLowerCase()
    );

    updateArrayItem(
      arrKey,
      i,
      match ? match.id : query
    );
  }, [query, peopleNames]);

  return (
    <span className='person-wrapper' ref={wrapperRef}>
      {suggestions.length > 0 && (
        <div className='suggestions-popup' ref={popupRef}>
          {suggestions.map((p) => (
            <div
              key={p.id}
              className='suggestions-item'
              onClick={() => handleSuggestionClick(p)}
            >
              {p.name}
            </div>
          ))}
        </div>  
      )}
      <input
        key={`${arrKey}-${i}`}
        type="text"
        value={query}
        onChange={handleChange}
        autoComplete='batman'
      />
    </span>
  )
}